import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import clsx from "clsx";

const headingVariants = cva("", {
  variants: {
    size: {
      h1: "text-4xl font-bold leading-tight",
      h2: "text-3xl font-bold",
      h3: "text-2xl font-semibold",
      h4: "text-xl font-semibold tracking-tight",
    },
  },
  defaultVariants: {
    size: "h1",
  },
});

export interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>, VariantProps<typeof headingVariants> {}

export function Heading({ className, size, ...props }: HeadingProps) {
  const Tag =
    size === "h1"
      ? "h1"
      : size === "h2"
        ? "h2"
        : size === "h3"
          ? "h3"
          : size === "h4"
            ? "h4"
            : "h1";
  return <Tag className={cn(headingVariants({ size }), className)} {...props} />;
}

const textStyleVariants = cva("", {
  variants: {
    gradient: {
      true: "bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent",
      false: "",
    },
    bold: {
      true: "text-purple-600 dark:text-purple-300",
      false: "",
    },
  },
  defaultVariants: {
    gradient: false,
    bold: false,
  },
});

interface FallbackTextProps {
  children: string | null | undefined;
  className?: string;
}

export function FallbackText({ children, className }: FallbackTextProps) {
  let display: string;
  let isDimmed = false;

  if (children === null) {
    display = "NULL";
    isDimmed = true;
  } else if (children === undefined) {
    display = "UNDEFINED";
    isDimmed = true;
  } else if (children === "") {
    display = "EMPTY STRING";
    isDimmed = true;
  } else {
    display = children;
  }

  return (
    <span className={clsx(className, isDimmed ? "text-gray-400 italic" : "text-inherit")}>
      {display}
    </span>
  );
}

export interface TextStyleProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof textStyleVariants> {
  children: React.ReactNode;
}

export function TextStyle({
  gradient = false,
  bold = false,
  className,
  children,
  ...props
}: TextStyleProps) {
  return (
    <span className={cn(textStyleVariants({ gradient, bold }), className)} {...props}>
      {children}
    </span>
  );
}

export interface ParaProps extends React.HTMLAttributes<HTMLParagraphElement> {
  size?: "2xl" | "xl" | "lg" | "md" | "sm" | "xs";
  muted?: boolean;
  center?: boolean;
}

const paraSizeClasses: Record<NonNullable<ParaProps["size"]>, string> = {
  "2xl": "text-2xl",
  xl: "text-xl",
  lg: "text-lg",
  md: "text-base",
  sm: "text-sm",
  xs: "text-xs",
};

// const paraSizeClasses: Record<NonNullable<ParaProps["size"]>, string> = {
//   "2xl": "text-2xl",
//   xl: "text-xl mb-8",
//   lg: "text-lg",
//   md: "text-base",
//   sm: "text-sm",
//   xs: "text-xs",
// };

export function Para({
  className,
  size = "md",
  muted = false,
  center = false,
  ...props
}: ParaProps) {
  return (
    <p
      className={cn(
        "max-w-2xl leading-relaxed",
        paraSizeClasses[size],
        muted ? "text-muted-foreground" : "text-foreground",
        center && "mx-auto",
        className,
      )}
      {...props}
    />
  );
}

type IconSize = "sm" | "md" | "lg";
type IconVariant = "default" | "secondary" | "muted";

interface IconProps extends React.SVGProps<SVGSVGElement> {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  size?: IconSize;
  variant?: IconVariant;
  className?: string;
}

const iconSizeClasses: Record<IconSize, string> = {
  sm: "w-4 h-4",
  md: "w-6 h-6",
  lg: "w-8 h-8",
};

const iconVariantClasses: Record<IconVariant, string> = {
  default: "",
  muted: "text-muted-foreground",
  secondary: "text-purple-600 dark:text-purple-300",
};

export function Icon({
  icon: LucideIcon,
  size = "md",
  variant = "default",
  className,
  ...props
}: IconProps) {
  return (
    <LucideIcon
      className={cn(iconSizeClasses[size], iconVariantClasses[variant], className)}
      {...props}
    />
  );
}

export { textStyleVariants };
export { headingVariants };
